/**
 * «Размещение»: чистые вычисления экрана менеджера — сводка для KPI, фильтры и страницы.
 *
 * Сервер отдаёт строки сезона страницами по 500 и не считает итогов, поэтому экран один раз
 * забирает весь сезон и дальше работает с ним в памяти: фильтр по хозяйству и культуре и
 * постраничный вывод по 50 строк — без повторных запросов. Здесь нет ни запросов, ни текста.
 */

import { localIsoDate } from "../../lib/localDate";
import type { PlacementRow } from "./placementApi";

/** Строк таблицы на странице. */
export const PAGE_SIZE = 50;

/** Окно «правок за неделю», дни. */
export const EDIT_WINDOW_DAYS = 7;

/** Значение фильтра хозяйства «без хозяйства» (строки, где реестр хозяйство не нашёл). */
export const FARM_NONE = "none";

export interface Filters {
  /** Код культуры; пусто — все. */
  crop: string;
  /** Пусто — все хозяйства; `FARM_NONE` — без хозяйства; иначе номер хозяйства строкой. */
  farm: string;
  /** Только правки за последнюю неделю. */
  recent: boolean;
}

export const NO_FILTERS: Filters = { crop: "", farm: "", recent: false };

export function hasFilters(filters: Filters): boolean {
  return filters.crop !== "" || filters.farm !== "" || filters.recent;
}

/** `ГГГГ-ММ-ДД` минус дни, по календарю (без часовых поясов). */
export function isoMinusDays(iso: string, days: number): string {
  const [y, mo, d] = iso.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, mo - 1, d - days));
  return date.toISOString().slice(0, 10);
}

/** Календарный день (по часам браузера) момента ввода строки; `null` — даты ввода у строки нет. */
function enteredDay(enteredAt: string | null | undefined): string | null {
  if (!enteredAt) return null;
  const at = new Date(enteredAt);
  return Number.isNaN(at.getTime()) ? null : localIsoDate(at);
}

/**
 * Правка за неделю (решение №1877): строка введена за последние 7 дней по дату среза
 * включительно (`entered_at`) И это правка, а не первичный ввод (`is_revision`, считает сервер):
 * она заменила другую строку (исправление ввода) либо фиксирует изменение на земле. Первичный
 * ввод — в том числе файлом на сотни строк — не считается. Строка без даты ввода (демо-слой,
 * прежний ввод) в счёт не входит.
 */
export function isRecentEdit(row: Pick<PlacementRow, "entered_at" | "is_revision">, today: string): boolean {
  if (!row.is_revision) return false;
  const day = enteredDay(row.entered_at);
  if (!day) return false;
  return day >= isoMinusDays(today, EDIT_WINDOW_DAYS - 1) && day <= today;
}

export interface CropTotal {
  code: string;
  /** Название, пришедшее с сервером, — на случай культуры вне словаря. */
  name: string;
  microha: number;
  rows: number;
}

export interface FarmChoice {
  id: number;
  name: string;
  rows: number;
}

export interface Summary {
  rows: number;
  microha: number;
  /** По убыванию площади. */
  crops: CropTotal[];
  noFarmRows: number;
  noFarmMicroha: number;
  recentRows: number;
  /** Хозяйства сезона по алфавиту — для списка с поиском. */
  farms: FarmChoice[];
}

export function summarize(rows: readonly PlacementRow[], today: string): Summary {
  const crops = new Map<string, CropTotal>();
  const farms = new Map<number, FarmChoice>();
  let microha = 0;
  let noFarmRows = 0;
  let noFarmMicroha = 0;
  let recentRows = 0;
  for (const row of rows) {
    const area = row.area_microha ?? 0;
    microha += area;
    const crop = crops.get(row.crop_code) ?? { code: row.crop_code, name: row.crop_name, microha: 0, rows: 0 };
    crop.microha += area;
    crop.rows += 1;
    crops.set(row.crop_code, crop);
    if (row.actor_id === null) {
      noFarmRows += 1;
      noFarmMicroha += area;
    } else {
      const farm = farms.get(row.actor_id) ?? { id: row.actor_id, name: row.actor_name ?? `#${row.actor_id}`, rows: 0 };
      farm.rows += 1;
      farms.set(row.actor_id, farm);
    }
    if (isRecentEdit(row, today)) recentRows += 1;
  }
  return {
    rows: rows.length,
    microha,
    crops: [...crops.values()].sort((a, b) => b.microha - a.microha || a.code.localeCompare(b.code)),
    noFarmRows,
    noFarmMicroha,
    recentRows,
    farms: [...farms.values()].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

export function applyFilters(rows: readonly PlacementRow[], filters: Filters, today: string): PlacementRow[] {
  if (!hasFilters(filters)) return rows.slice();
  return rows.filter((row) => {
    if (filters.crop && row.crop_code !== filters.crop) return false;
    if (filters.farm === FARM_NONE && row.actor_id !== null) return false;
    if (filters.farm && filters.farm !== FARM_NONE && String(row.actor_id) !== filters.farm) return false;
    if (filters.recent && !isRecentEdit(row, today)) return false;
    return true;
  });
}

export interface Page<T> {
  items: T[];
  /** Номер первой и последней строки страницы, с единицы; при пустом списке — нули. */
  from: number;
  to: number;
  page: number;
  pages: number;
}

/** Страница `page` (с нуля); номер за пределами списка прижимается к последней странице. */
export function pageOf<T>(rows: readonly T[], page: number, size: number = PAGE_SIZE): Page<T> {
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const current = Math.min(Math.max(0, page), pages - 1);
  const start = current * size;
  const items = rows.slice(start, start + size);
  return {
    items,
    from: items.length === 0 ? 0 : start + 1,
    to: start + items.length,
    page: current,
    pages,
  };
}

/**
 * Карточки KPI по культурам: до трёх штук. Если культур больше, третья — «остальные»
 * (код пустой: по ней не фильтруют).
 */
export function cropCards(crops: readonly CropTotal[], max = 3): CropTotal[] {
  if (crops.length <= max) return crops.slice();
  const head = crops.slice(0, max - 1);
  const rest = crops.slice(max - 1);
  return [
    ...head,
    {
      code: "",
      name: "",
      microha: rest.reduce((sum, c) => sum + c.microha, 0),
      rows: rest.reduce((sum, c) => sum + c.rows, 0),
    },
  ];
}
